import React, { useEffect, useRef, useState } from 'react';
import {
    Activity,
    AlertCircle,
    Award,
    BookOpen,
    Calendar,
    Camera,
    CheckCircle,
    Crosshair,
    FileText,
    Flower2,
    GraduationCap,
    Heart,
    HeartPulse,
    Home,
    Languages,
    Lightbulb,
    Lock,
    Mail,
    MapPin,
    MessageSquare,
    PartyPopper,
    Phone,
    RefreshCw,
    Ribbon,
    Save,
    School,
    Sparkles,
    Star,
    Target,
    User,
    Users,
    X
} from 'lucide-react';
import Layout from "../Layout";
import { API_CONFIG } from "../config/api";
import BeneficiaryFeedbackCard from "../Components/BeneficiaryFeedbackCard";
//
// // Helper Components (defined outside the main component to prevent re-rendering issues)
// const InputField = ({label, name, value, onChange, placeholder, type = "text", icon: Icon}) => (
//     <div className="space-y-2">
//         <label className="text-sm font-semibold text-gray-700 flex items-center">{Icon &&
//             <Icon size={14} className="mr-2 text-gray-400"/>}{label}</label>
//         <input type={type} name={name} value={value ?? ''} onChange={onChange} placeholder={placeholder}
//                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent h-12"/>
//     </div>
// );
//
// const TextAreaField = ({label, name, value, onChange, placeholder, rows = 3, icon: Icon, }) => (
//     <div className="space-y-2">
//         <label className="text-sm font-semibold text-gray-700 flex items-center">{Icon &&
//             <Icon size={14} className="mr-2 text-gray-400"/>}{label}</label>
//         <textarea name={name}  value={value ?? ''} onChange={onChange} placeholder={placeholder} rows={rows}
//                   className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent resize-y min-h-[48px]"/>
//         {(name.includes('attendance')) &&
//             <p className="text-xs text-gray-500">Enter in JSON format, e.g. {`{"APR": 20, "MAY": 22}`}</p>}
//         {(name.includes('learnings') || name.includes('skills') || name.includes('subjects') || name.includes('habits') || name.includes('siblings') || name.includes('targets')) &&
//             <p className="text-xs text-gray-500">Enter items separated by commas.</p>}
//     </div>
// );

// TextAreaField.jsx
const TextAreaField = ({
    label,
    name,
    value,
    onChange,
    placeholder,
    rows = 3,
    icon: Icon,
    disabled: forceDisabled = false      // NEW
}) => {
    // auto-disable only working days and days attended fields (NOT low_reason)
    const isAttendanceReadOnly = name === 'attendance_working_days' || name === 'attendance_days_attended';
    const disabled = forceDisabled || isAttendanceReadOnly;

    return (
        <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 flex items-center">
                {Icon && <Icon size={14} className="mr-2 text-gray-400" />}
                {label}
            </label>

            <textarea
                name={name}
                value={value ?? ''}
                onChange={onChange}
                placeholder={placeholder}
                rows={rows}
                disabled={disabled}                              // NEW
                className={`w-full px-4 py-3 rounded-lg resize-y min-h-[48px]
          bg-gray-50 border border-gray-200
          focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent
          ${disabled && 'opacity-60 cursor-not-allowed'}`}            // visual cue
            />

            {name.includes('attendance') && (
                <p className="text-xs text-gray-500">
                    Enter in JSON format, e.g. {"{\"APR\": 20, \"MAY\": 22}"}
                </p>
            )}
            {(name.match(/learnings|skills|subjects|habits|siblings|targets/)) && (
                <p className="text-xs text-gray-500">Enter items separated by commas.</p>
            )}
        </div>
    );
};


// InputField.jsx
const InputField = ({
    label,
    name,
    value,
    onChange,
    placeholder,
    type = 'text',
    icon: Icon,
    disabled: forceDisabled = false      // NEW
}) => {
    const isAttendance = name?.toLowerCase().includes('attendance');
    const disabled = forceDisabled || isAttendance;

    return (
        <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 flex items-center">
                {Icon && <Icon size={14} className="mr-2 text-gray-400" />}
                {label}
            </label>

            <input
                type={type}
                name={name}
                value={value ?? ''}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}                              // NEW
                className={`w-full px-4 py-3 rounded-lg h-12
          bg-gray-50 border border-gray-200
          focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent
          ${disabled && 'opacity-60 cursor-not-allowed'}`}            // visual cue
            />
        </div>
    );
};


const ProfilePictureModal = ({ show, onClose, onUpload, uploading, currentPicture, getInitials, name }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleClose = () => {
        onClose();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    if (!show) return null;

    return (
        <div
            className="font-[poppins] fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-slate-900">Upload Picture</h2>
                    <button onClick={handleClose} disabled={uploading} className="p-2 rounded-xl hover:bg-gray-100"><X
                        size={24} className="text-slate-400" /></button>
                </div>
                <div className="space-y-6">
                    <div className="flex justify-center">
                        <img
                            src={previewUrl || currentPicture || `https://placehold.co/128x128/D67C2C/FFFFFF?text=${getInitials(name)}`}
                            alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-[#D67C2C]" />
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect}
                        className="hidden" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed border-[#D67C2C] rounded-2xl text-[#D67C2C] hover:bg-orange-50">
                        <Camera size={20} />
                        <span className="font-medium">{selectedFile ? "Change Picture" : "Choose Picture"}</span>
                    </button>
                    <div className="flex space-x-4">
                        <button onClick={handleClose} disabled={uploading}
                            className="flex-1 px-6 py-3 border rounded-xl hover:bg-slate-50">Cancel
                        </button>
                        <button onClick={() => onUpload(selectedFile)} disabled={!selectedFile || uploading}
                            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#D67C2C] text-white rounded-xl disabled:opacity-50">
                            {uploading ? <><RefreshCw size={16} className="animate-spin" /> Uploading...</> : <><Save
                                size={16} /> Upload</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


const StudentProfilePage = () => {

    const GRADE_LIST = [
        "BV1", "BV2", "BV3", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"
    ];

    const server_url = API_CONFIG.BASE_URL;
    const accessToken = localStorage.getItem("accessToken");

    const apiService = {
        // ... (all api service functions remain the same)
        uploadProfilePicture: async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${server_url}/users/me/profile-picture`, {
                method: "PUT",
                headers: { "Authorization": `Bearer ${accessToken}` },
                body: formData
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to upload profile picture");
            }
            return response.json();
        },
        getProfilePicture: async () => {
            const response = await fetch(`${server_url}/users/me/profile-picture`, {
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error("Failed to get profile picture");
            }
            return response.json();
        },
        uploadFamilyPicture: async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${server_url}/users/me/family-picture`, {
                method: "PUT",
                headers: { "Authorization": `Bearer ${accessToken}` },
                body: formData
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to upload family picture");
            }
            return response.json();
        },
        getFamilyPicture: async () => {
            const response = await fetch(`${server_url}/users/me/family-picture`, {
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error("Failed to get family picture");
            }
            return response.json();
        },
        fetchUserData: async () => {
            const response = await fetch(`${server_url}/user/me`, {
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (!response.ok) throw new Error("Failed to fetch user data");
            return response.json();
        },
        fetchStudentProfile: async () => {
            const response = await fetch(`${server_url}/student/profile`, {
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response.status === 404) return null;
            if (!response.ok) throw new Error("Failed to fetch student profile");
            return response.json();
        },
        updateStudentProfile: async (data) => {
            const response = await fetch(`${server_url}/student/update`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` },
                body: JSON.stringify(data)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to update profile");
            }
            return response.json();
        }
    };

    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [notification, setNotification] = useState(null);
    const [passwordShown, setPasswordShown] = useState(false);
    const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
    const [activityData, setActivityData] = useState([]);
    const [profilePicture, setProfilePicture] = useState(null);
    const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const [familyPicture, setFamilyPicture] = useState(null);
    const [showFamilyPictureModal, setShowFamilyPictureModal] = useState(false);
    const [uploadingFamilyPicture, setUploadingFamilyPicture] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        school_name: "",
        village: "",
        brc: "",
        crc: "",
        state: "",
        pin_code: "",
        udise_code: "",
        teacher_code: "",
        apaar_id: "",
        roll_no: "",
        registration_no: "",
        class_grade: "",
        section: "",
        date_of_birth: "",
        address: "",
        phone: "",
        age_years: "",
        mother_name: "",
        mother_education: "",
        mother_occupation: "",
        father_name: "",
        father_education: "",
        father_occupation: "",
        number_of_siblings: "",
        siblings_age: "",
        mother_tongue: "",
        medium_of_instruction: "",
        rural_or_urban: "",
        times_fallen_ill: "",
        attendance_working_days: "",
        attendance_days_attended: "",
        attendance_low_reason: "",
        live_with: "",
        stay_at: "",
        free_time_activity: "",
        do_very_well: "",
        responsible: "",
        could_do_better: "",
        show_care_by: "",
        proud_when: "",
        good_at: "",
        not_good_at: "",
        would_like_to_improve: "",
        like_to: "",
        dont_like_to: "",
        favourite_colours: "",
        favourite_foods: "",
        favourite_games: "",
        favourite_festivals: "",
        favourite_flowers: "",
        favourite_sports: "",
        favourite_animals: "",
        favourite_subjects: "",
        want_to_be: "",
        idol: "",
        learn_targets: "",
        family_picture_url: "",
        birthday: "",
        friends_are: "",
        academic_goal: "",
        academic_goal_importance: "",
        academic_goal_step1: "",
        academic_goal_step2: "",
        personal_goal: "",
        personal_goal_importance: "",
        personal_goal_step1: "",
        personal_goal_step2: "",
        learnings_school: "",
        learnings_outside_school: "",
        teacher_help_with: "",
        teacher_help_details: "",
        teacher_to_know: "",
        teacher_know_details: "",
        ambition: "",
        skills_to_achieve: "",
        achieve_by: "",
        focus_subjects: "",
        habits_for_ambition: "",
        guidance_from: "",
        help_by_person: "",
        learn_new: "",
        feel_when_achieved: "",
        parents_feel_when_achieved: "",
    });

    const showNotification = (message, type = "success") => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    const getInitials = (name) => !name ? "?" : name.split(' ').map(n => n[0]).join('').toUpperCase();
    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === "number" && value !== "" ? parseFloat(value) : value }));
    };

    useEffect(() => {
        const loadAllData = async () => {
            setLoading(true);
            try {
                const [userData, profileData, pictureData, familyPictureData] = await Promise.all([
                    apiService.fetchUserData(),
                    apiService.fetchStudentProfile(),
                    apiService.getProfilePicture(),
                    apiService.getFamilyPicture()
                ]);

                if (pictureData) setProfilePicture(server_url + pictureData.profile_picture_url);
                if (familyPictureData) setFamilyPicture(server_url + familyPictureData.family_picture_url);

                let combinedData = { ...formData };

                if (userData) {
                    combinedData.name = userData.name || "";
                    combinedData.email = userData.email || "";
                    setActivityData(userData.activities || []);
                }

                if (profileData) {
                    const formatForDateInput = (d) => d ? d.split('T')[0] : "";
                    const formatArray = (arr) => Array.isArray(arr) ? arr.join(', ') : "";
                    const formatDict = (dict) => dict ? JSON.stringify(dict, null, 2) : "";

                    for (const key in combinedData) {
                        if (profileData.hasOwnProperty(key)) {
                            if (key === 'date_of_birth' || key === 'birthday') {
                                combinedData[key] = formatForDateInput(profileData[key]);
                            } else if (Array.isArray(profileData[key])) {
                                combinedData[key] = formatArray(profileData[key]);
                            } else if (typeof profileData[key] === 'object' && profileData[key] !== null) {
                                combinedData[key] = formatDict(profileData[key]);
                            } else {
                                combinedData[key] = profileData[key] ?? "";
                            }
                        }
                    }
                    // Ensure name from profile takes precedence if it exists
                    combinedData.name = profileData.name || combinedData.name;
                }
                setFormData(combinedData);

            } catch (error) {
                showNotification(`Error loading profile: ${error.message}`, "error");
            } finally {
                setLoading(false);
            }
        };
        loadAllData();
    }, []);

    const updateProfile = async (e) => {
        e.preventDefault();
        if (formData.password && formData.password !== formData.confirmPassword) {
            return showNotification("Passwords do not match", "error");
        }
        setSaveLoading(true);
        try {
            const updatedData = {};
            const parseToList = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : undefined;
            const parseToDict = (str) => {
                try {
                    return str ? JSON.parse(str) : undefined;
                } catch {
                    throw new Error("Invalid JSON format for attendance fields.");
                }
            };

            const listFields = ['siblings_age', 'learn_targets', 'learnings_school', 'learnings_outside_school', 'skills_to_achieve', 'focus_subjects', 'habits_for_ambition'];
            const dictFields = ['attendance_working_days', 'attendance_days_attended'];

            for (const key in formData) {
                if (key === 'confirmPassword') continue;

                const value = formData[key];
                if (value !== "" && value !== null) {
                    if (listFields.includes(key)) updatedData[key] = parseToList(value);
                    else if (dictFields.includes(key)) updatedData[key] = parseToDict(value);
                    else updatedData[key] = value;
                }
            }

            await apiService.updateStudentProfile(updatedData);
            showNotification("Profile updated successfully!");
            setFormData(prev => ({ ...prev, password: "", confirmPassword: "" }));
        } catch (error) {
            showNotification(error.message || "An error occurred during update.", "error");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleProfilePictureUpload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return showNotification("File size must be less than 5MB", "error");

        setUploadingPicture(true);
        try {
            const result = await apiService.uploadProfilePicture(file);
            setProfilePicture(server_url + result.profile_picture_url);
            setShowProfilePictureModal(false);
            showNotification("Profile picture updated successfully!", "success");
        } catch (error) {
            showNotification(`Upload failed: ${error.message}`, "error");
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleFamilyPictureUpload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return showNotification("File size must be less than 5MB", "error");

        setUploadingFamilyPicture(true);
        try {
            const result = await apiService.uploadFamilyPicture(file);
            setFamilyPicture(server_url + result.family_picture_url);
            setShowFamilyPictureModal(false);
            showNotification("Family picture updated successfully!", "success");
        } catch (error) {
            showNotification(`Upload failed: ${error.message}`, "error");
        } finally {
            setUploadingFamilyPicture(false);
        }
    };

    // Helper to check if student is in Middle Stage (Grades 6-8)
    const isMiddleStage = () => {
        const grade = formData.class_grade;
        return grade === '6' || grade === '7' || grade === '8';
    };

    const allTabs = [
        { key: 'personal', icon: User, label: 'Personal' },
        { key: 'academic', icon: GraduationCap, label: 'Academic' },
        { key: 'family', icon: Users, label: 'Family & Health' },
        { key: 'about_me', icon: MessageSquare, label: 'About Me' },
        { key: 'favourites', icon: Star, label: 'Favourites' },
        { key: 'goals', icon: Target, label: 'Goals & Ambitions' },
        { key: 'activities', icon: Activity, label: 'Activities' }
    ];

    // Only show Goals & Ambitions tab for Middle Stage (grades 6-8)
    const tabs = allTabs.filter(tab => tab.key !== 'goals' || isMiddleStage());

    return (
        <Layout>
            <div className="font-[poppins] bg-gray-50 min-h-full">
                <div className="w-full mx-auto sm:px-6 lg:px-8 py-12">
                    {loading ? <div className="flex justify-center items-center h-96"><RefreshCw
                        className="w-16 h-16 text-[#D67C2C] animate-spin" /></div> : (
                        <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
                            {/* Sidebar Card */}
                            <div className="relative w-full lg:w-80 flex-shrink-0">
                                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                                    <div
                                        className="bg-[#D67C2C] text-white px-8 py-3 rounded-xl font-bold text-lg shadow-lg">Menu
                                    </div>
                                </div>
                                <div
                                    className="absolute inset-0 bg-orange-300 rounded-2xl transform translate-x-2 translate-y-2"></div>
                                <div
                                    className="relative bg-white rounded-2xl border-2 border-gray-800 shadow-xl overflow-hidden">
                                    <div className="bg-[#D67C2C] px-6 py-12 text-center text-white">
                                        <div className="relative inline-block group">
                                            <img
                                                src={profilePicture || `https://placehold.co/96x96/FFFFFF/D67C2C?text=${getInitials(formData.name)}`}
                                                alt="Profile"
                                                className="w-24 h-24 rounded-full object-cover mb-4 mx-auto border-4 border-white shadow-lg" />
                                            <button onClick={() => setShowProfilePictureModal(true)}
                                                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white mb-4">
                                                <Camera size={20} /></button>
                                        </div>
                                        <h2 className="text-xl font-bold">{formData.name}</h2>
                                        <p className="text-sm text-orange-100 mb-3">{formData.email}</p>
                                        <div
                                            className="inline-flex items-center bg-white/20 px-4 py-2 rounded-full text-sm font-medium">
                                            <GraduationCap size={16}
                                                className="mr-2" />{formData.class_grade || 'N/A'} - {formData.section || 'N/A'}
                                        </div>
                                    </div>
                                    <nav className="p-6 space-y-2">
                                        {tabs.map(({ key, icon: Icon, label }) => (
                                            <button key={key} onClick={() => setActiveTab(key)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition-all ${activeTab === key ? 'bg-orange-50 text-[#D67C2C] shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                                                <Icon size={20}
                                                    className={activeTab === key ? 'text-[#D67C2C]' : 'text-gray-400'} /><span
                                                        className="text-sm">{label}</span>
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="relative flex-1 max-w-4xl">
                                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                                    <div
                                        className="bg-[#D67C2C] text-white px-8 py-3 rounded-xl font-bold text-lg shadow-lg whitespace-nowrap">{tabs.find(t => t.key === activeTab)?.label}</div>
                                </div>
                                <div
                                    className="absolute inset-0 bg-orange-300 rounded-2xl transform translate-x-2 translate-y-2"></div>
                                <div
                                    className="relative bg-white rounded-2xl border-2 border-gray-800 shadow-xl min-h-[600px]">
                                    {notification && (<div
                                        className={`m-6 p-4 rounded-lg flex items-center gap-3 ${notification.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>{notification.type === 'error' ?
                                            <AlertCircle size={20} /> : <CheckCircle size={20} />}<p
                                                className="font-medium">{notification.message}</p></div>)}

                                    <div className="p-8">
                                        <form onSubmit={updateProfile}>
                                            {activeTab === 'personal' && <div className="grid md:grid-cols-2 gap-6">
                                                <InputField label="Name" name="name" value={formData.name}
                                                    onChange={handleInputChange} icon={User} />
                                                <div className="space-y-2"><label
                                                    className="text-sm font-semibold text-gray-700 flex items-center"><Mail
                                                        size={14} className="mr-2 text-gray-400" />Email</label><input
                                                        type="email" value={formData.email}
                                                        className="w-full px-4 py-3 bg-gray-100 border rounded-lg cursor-not-allowed h-12"
                                                        disabled /></div>
                                                <InputField label="New Password" name="password"
                                                    type={passwordShown ? "text" : "password"}
                                                    value={formData.password} onChange={handleInputChange}
                                                    placeholder="Leave blank to keep current" icon={Lock} />
                                                <InputField label="Confirm Password" name="confirmPassword"
                                                    type={confirmPasswordShown ? "text" : "password"}
                                                    value={formData.confirmPassword}
                                                    onChange={handleInputChange}
                                                    placeholder="Confirm new password" icon={Lock} />
                                                <InputField label="Phone Number" name="phone" value={formData.phone}
                                                    onChange={handleInputChange} icon={Phone} />
                                                <InputField label="Date of Birth" name="date_of_birth" type="date"
                                                    value={formData.date_of_birth} onChange={handleInputChange}
                                                    icon={Calendar} />
                                                <InputField label="Age (Years)" name="age_years" type="number"
                                                    value={formData.age_years} onChange={handleInputChange}
                                                    icon={User} />
                                                <div className="md:col-span-2"><TextAreaField label="Address"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    icon={Home} /></div>
                                            </div>}

                                            {activeTab === 'academic' && <div className="grid md:grid-cols-2 gap-6">
                                                <InputField label="School Name" name="school_name"
                                                    value={formData.school_name} onChange={handleInputChange}
                                                    icon={School} />
                                                <InputField label="Village / Area" name="village"
                                                    value={formData.village} onChange={handleInputChange}
                                                    icon={MapPin} />
                                                <InputField label="BRC" name="brc" value={formData.brc}
                                                    onChange={handleInputChange} icon={MapPin} />
                                                <InputField label="CRC" name="crc" value={formData.crc}
                                                    onChange={handleInputChange} icon={MapPin} />
                                                <InputField label="State" name="state" value={formData.state}
                                                    onChange={handleInputChange} icon={MapPin} />
                                                <InputField label="PIN Code" name="pin_code" value={formData.pin_code}
                                                    onChange={handleInputChange} icon={MapPin} />
                                                <InputField label="UDISE Code" name="udise_code"
                                                    value={formData.udise_code} onChange={handleInputChange}
                                                    icon={FileText} />
                                                <InputField label="Teacher Code" name="teacher_code"
                                                    value={formData.teacher_code} onChange={handleInputChange}
                                                    icon={FileText} />
                                                <InputField label="APAAR ID" name="apaar_id" value={formData.apaar_id}
                                                    onChange={handleInputChange} icon={FileText} />
                                                <InputField label="Roll Number" name="roll_no" value={formData.roll_no}
                                                    onChange={handleInputChange} icon={FileText} />
                                                <InputField label="Registration No." name="registration_no"
                                                    value={formData.registration_no}
                                                    onChange={handleInputChange} icon={FileText} />
                                                {/*<InputField label="Class / Grade" name="class_grade"*/}
                                                {/*            value={formData.class_grade} onChange={handleInputChange}*/}
                                                {/*            icon={GraduationCap}/>*/}

                                                <div className="space-y-2">
                                                    <label
                                                        className="text-sm font-semibold text-gray-700 flex items-center">
                                                        <GraduationCap size={14} className="mr-2 text-gray-400" />
                                                        Class / Grade
                                                    </label>
                                                    <select
                                                        name="class_grade"
                                                        value={formData.class_grade}
                                                        onChange={handleInputChange}
                                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent h-12"
                                                        required
                                                    >
                                                        <option value="">Select grade</option>
                                                        {GRADE_LIST.map((g) => (
                                                            <option key={g} value={g}>Grade {g}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <InputField label="Section" name="section" value={formData.section}
                                                    onChange={handleInputChange} icon={GraduationCap} />
                                                <InputField label="Mother Tongue" name="mother_tongue"
                                                    value={formData.mother_tongue} onChange={handleInputChange}
                                                    icon={Languages} />
                                                <InputField label="Medium of Instruction" name="medium_of_instruction"
                                                    value={formData.medium_of_instruction}
                                                    onChange={handleInputChange} icon={BookOpen} />
                                                <InputField label="Location Type (Rural/Urban)" name="rural_or_urban"
                                                    value={formData.rural_or_urban} onChange={handleInputChange}
                                                    icon={Home} />
                                            </div>}

                                            {activeTab === 'family' && <div className="space-y-8">
                                                <div className="p-6 bg-pink-50 rounded-lg border border-pink-200">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                        <Heart size={20} className="text-pink-500" />Mother's Details
                                                    </h3>
                                                    <div className="grid md:grid-cols-3 gap-4">
                                                        <InputField label="Name" name="mother_name"
                                                            value={formData.mother_name}
                                                            onChange={handleInputChange} /><InputField
                                                            label="Education" name="mother_education"
                                                            value={formData.mother_education} onChange={handleInputChange} /><InputField
                                                            label="Occupation" name="mother_occupation"
                                                            value={formData.mother_occupation}
                                                            onChange={handleInputChange} />
                                                    </div>
                                                </div>
                                                <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                        <Users size={20} className="text-blue-500" />Father's Details
                                                    </h3>
                                                    <div className="grid md:grid-cols-3 gap-4">
                                                        <InputField label="Name" name="father_name"
                                                            value={formData.father_name}
                                                            onChange={handleInputChange} /><InputField
                                                            label="Education" name="father_education"
                                                            value={formData.father_education} onChange={handleInputChange} /><InputField
                                                            label="Occupation" name="father_occupation"
                                                            value={formData.father_occupation}
                                                            onChange={handleInputChange} />
                                                    </div>
                                                </div>
                                                <div className="grid md:grid-cols-2 gap-6">
                                                    <InputField label="Number of Siblings" name="number_of_siblings"
                                                        type="number" value={formData.number_of_siblings}
                                                        onChange={handleInputChange} /><TextAreaField
                                                        label="Siblings' Ages" name="siblings_age"
                                                        value={formData.siblings_age} onChange={handleInputChange}
                                                        placeholder="e.g. 10, 15" />
                                                </div>
                                                <div className="grid md:grid-cols-1 gap-6 pt-6 border-t"><InputField
                                                    label="Times Fallen Ill (last 6 months)" name="times_fallen_ill"
                                                    type="number" value={formData.times_fallen_ill}
                                                    onChange={handleInputChange} icon={HeartPulse} /></div>
                                                <div className="grid md:grid-cols-2 gap-6">
                                                    <TextAreaField
                                                        label="Working Days / Month" name="attendance_working_days"
                                                        value={formData.attendance_working_days}
                                                        onChange={handleInputChange} icon={Calendar} />
                                                    <TextAreaField
                                                        label="Days Attended / Month" name="attendance_days_attended"
                                                        value={formData.attendance_days_attended}
                                                        onChange={handleInputChange} icon={CheckCircle} /></div>
                                                <div className="grid md:grid-cols-1 gap-6"><TextAreaField
                                                    label="Reason for Low Attendance" name="attendance_low_reason"
                                                    value={formData.attendance_low_reason} onChange={handleInputChange}
                                                    icon={AlertCircle} /></div>
                                            </div>}

                                            {activeTab === 'about_me' && (
                                                <div className="space-y-8">
                                                    {/* Foundation Section */}
                                                    <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                            <Sparkles size={20} className="text-blue-500" />Foundation
                                                            Info
                                                        </h3>
                                                        <div className="grid md:grid-cols-2 gap-6">
                                                            <InputField label="I am good at..." name="good_at"
                                                                value={formData.good_at}
                                                                onChange={handleInputChange}
                                                                icon={Award} />
                                                            <InputField label="I am not so good at..."
                                                                name="not_good_at"
                                                                value={formData.not_good_at}
                                                                onChange={handleInputChange} icon={Crosshair} />
                                                            <InputField label="I would like to improve..."
                                                                name="would_like_to_improve"
                                                                value={formData.would_like_to_improve}
                                                                onChange={handleInputChange} icon={Target} />
                                                            <InputField label="I like to..." name="like_to"
                                                                value={formData.like_to}
                                                                onChange={handleInputChange}
                                                                icon={Heart} />
                                                            <InputField label="I don't like to..." name="dont_like_to"
                                                                value={formData.dont_like_to}
                                                                onChange={handleInputChange} icon={X} />
                                                            <InputField label="My idol is..." name="idol"
                                                                value={formData.idol}
                                                                onChange={handleInputChange} icon={Star} />
                                                            <InputField label="I want to be a..." name="want_to_be"
                                                                value={formData.want_to_be}
                                                                onChange={handleInputChange}
                                                                icon={Ribbon} />
                                                            <InputField label="My friends are..." name="friends_are"
                                                                value={formData.friends_are}
                                                                onChange={handleInputChange} icon={Users} />
                                                            <InputField label="My Birthday is on..." name="birthday"
                                                                type="date"
                                                                value={formData.birthday}
                                                                onChange={handleInputChange}
                                                                icon={Calendar} />
                                                            {/* Family Picture Upload */}
                                                            <div className="space-y-2">
                                                                <label className="text-sm font-semibold text-gray-700 flex items-center">
                                                                    <Camera size={14} className="mr-2 text-gray-400" />
                                                                    Family Picture
                                                                </label>
                                                                <div className="flex items-center gap-4">
                                                                    {familyPicture ? (
                                                                        <img
                                                                            src={familyPicture}
                                                                            alt="Family"
                                                                            className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                                                                            <Users size={24} className="text-gray-400" />
                                                                        </div>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowFamilyPictureModal(true)}
                                                                        className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-[#D67C2C] rounded-lg text-[#D67C2C] hover:bg-orange-50 transition-colors"
                                                                    >
                                                                        <Camera size={16} />
                                                                        <span className="font-medium">{familyPicture ? 'Change' : 'Upload'}</span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <TextAreaField
                                                                    label="I want to learn..." name="learn_targets"
                                                                    value={formData.learn_targets}
                                                                    onChange={handleInputChange}
                                                                    icon={Lightbulb} />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* General Section */}
                                                    <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                            <MessageSquare size={20} className="text-green-500" />General
                                                            Info
                                                        </h3>
                                                        <div className="grid md:grid-cols-2 gap-6">
                                                            <TextAreaField label="I live with" name="live_with"
                                                                value={formData.live_with}
                                                                onChange={handleInputChange} icon={Home} />
                                                            <TextAreaField label="I stay at" name="stay_at"
                                                                value={formData.stay_at}
                                                                onChange={handleInputChange}
                                                                icon={Home} />
                                                            <TextAreaField label="In my free time, I like to"
                                                                name="free_time_activity"
                                                                value={formData.free_time_activity}
                                                                onChange={handleInputChange}
                                                                icon={Sparkles} />
                                                            <TextAreaField label="Something I do very well"
                                                                name="do_very_well"
                                                                value={formData.do_very_well}
                                                                onChange={handleInputChange} icon={Award} />
                                                            <TextAreaField label="I am responsible for"
                                                                name="responsible"
                                                                value={formData.responsible}
                                                                onChange={handleInputChange}
                                                                icon={CheckCircle} />
                                                            <TextAreaField label="I think I could do better in"
                                                                name="could_do_better"
                                                                value={formData.could_do_better}
                                                                onChange={handleInputChange}
                                                                icon={Crosshair} />
                                                            <TextAreaField label="I show care for others by"
                                                                name="show_care_by"
                                                                value={formData.show_care_by}
                                                                onChange={handleInputChange} icon={Heart} />
                                                            <TextAreaField label="I feel proud when" name="proud_when"
                                                                value={formData.proud_when}
                                                                onChange={handleInputChange}
                                                                icon={Sparkles} />
                                                        </div>
                                                    </div>

                                                    {/* Learning Section */}
                                                    <div
                                                        className="p-6 bg-yellow-50 rounded-lg border border-yellow-200">
                                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                            <School size={20} className="text-yellow-500" />Learning &
                                                            Teacher Interaction
                                                        </h3>
                                                        <div className="grid md:grid-cols-2 gap-6">
                                                            <div className="md:col-span-2">
                                                                <TextAreaField
                                                                    label="Learnings in School" name="learnings_school"
                                                                    value={formData.learnings_school}
                                                                    onChange={handleInputChange}
                                                                    icon={School} />
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <TextAreaField
                                                                    label="Learnings Outside School"
                                                                    name="learnings_outside_school"
                                                                    value={formData.learnings_outside_school}
                                                                    onChange={handleInputChange} icon={Home} />
                                                            </div>
                                                            <InputField label="I need my teacher's help with"
                                                                name="teacher_help_with"
                                                                value={formData.teacher_help_with}
                                                                onChange={handleInputChange} icon={Lightbulb} />
                                                            <TextAreaField label="Details about help needed"
                                                                name="teacher_help_details"
                                                                value={formData.teacher_help_details}
                                                                onChange={handleInputChange}
                                                                icon={MessageSquare} />
                                                            <InputField label="I want my teacher to know"
                                                                name="teacher_to_know"
                                                                value={formData.teacher_to_know}
                                                                onChange={handleInputChange} icon={Lightbulb} />
                                                            <TextAreaField label="Details for teacher"
                                                                name="teacher_know_details"
                                                                value={formData.teacher_know_details}
                                                                onChange={handleInputChange}
                                                                icon={MessageSquare} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'favourites' && <div className="grid md:grid-cols-2 gap-6">
                                                <InputField label="Favourite Colours" name="favourite_colours"
                                                    value={formData.favourite_colours}
                                                    onChange={handleInputChange} icon={Sparkles} />
                                                <InputField label="Favourite Foods" name="favourite_foods"
                                                    value={formData.favourite_foods}
                                                    onChange={handleInputChange} icon={Sparkles} />
                                                <InputField label="Favourite Games" name="favourite_games"
                                                    value={formData.favourite_games}
                                                    onChange={handleInputChange} icon={Sparkles} />
                                                <InputField label="Favourite Festivals" name="favourite_festivals"
                                                    value={formData.favourite_festivals}
                                                    onChange={handleInputChange} icon={PartyPopper} />
                                                <InputField label="Favourite Flowers" name="favourite_flowers"
                                                    value={formData.favourite_flowers}
                                                    onChange={handleInputChange} icon={Flower2} />
                                                <InputField label="Favourite Sports" name="favourite_sports"
                                                    value={formData.favourite_sports}
                                                    onChange={handleInputChange} icon={Sparkles} />
                                                <InputField label="Favourite Animals" name="favourite_animals"
                                                    value={formData.favourite_animals}
                                                    onChange={handleInputChange} icon={Sparkles} />
                                                <InputField label="Favourite Subjects" name="favourite_subjects"
                                                    value={formData.favourite_subjects}
                                                    onChange={handleInputChange} icon={BookOpen} />
                                            </div>}

                                            {activeTab === 'goals' && <div className="space-y-8">
                                                <div className="p-6 bg-green-50 rounded-lg border border-green-200"><h3
                                                    className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                    <GraduationCap size={20} className="text-green-500" />Academic Goals
                                                </h3>
                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        <div className="md:col-span-2"><InputField label="Goal"
                                                            name="academic_goal"
                                                            value={formData.academic_goal}
                                                            onChange={handleInputChange} />
                                                        </div>
                                                        <InputField label="Importance" name="academic_goal_importance"
                                                            value={formData.academic_goal_importance}
                                                            onChange={handleInputChange} /><InputField
                                                            label="Step 1" name="academic_goal_step1"
                                                            value={formData.academic_goal_step1}
                                                            onChange={handleInputChange} /><InputField label="Step 2"
                                                                name="academic_goal_step2"
                                                                value={formData.academic_goal_step2}
                                                                onChange={handleInputChange} />
                                                    </div>
                                                </div>
                                                <div className="p-6 bg-purple-50 rounded-lg border border-purple-200">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                        <User size={20} className="text-purple-500" />Personal Goals</h3>
                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        <div className="md:col-span-2"><InputField label="Goal"
                                                            name="personal_goal"
                                                            value={formData.personal_goal}
                                                            onChange={handleInputChange} />
                                                        </div>
                                                        <InputField label="Importance" name="personal_goal_importance"
                                                            value={formData.personal_goal_importance}
                                                            onChange={handleInputChange} /><InputField
                                                            label="Step 1" name="personal_goal_step1"
                                                            value={formData.personal_goal_step1}
                                                            onChange={handleInputChange} /><InputField label="Step 2"
                                                                name="personal_goal_step2"
                                                                value={formData.personal_goal_step2}
                                                                onChange={handleInputChange} />
                                                    </div>
                                                </div>
                                                <div className="p-6 bg-blue-50 rounded-lg border border-blue-200"><h3
                                                    className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                    <Target size={20} className="text-blue-500" />Ambition Card</h3>
                                                    <div className="grid md:grid-cols-2 gap-4"><InputField
                                                        label="My Ambition" name="ambition" value={formData.ambition}
                                                        onChange={handleInputChange} /><InputField
                                                            label="To achieve this, I will learn" name="achieve_by"
                                                            value={formData.achieve_by}
                                                            onChange={handleInputChange} /><TextAreaField
                                                            label="Skills to Achieve" name="skills_to_achieve"
                                                            value={formData.skills_to_achieve}
                                                            onChange={handleInputChange} /><TextAreaField
                                                            label="Subjects to Focus On" name="focus_subjects"
                                                            value={formData.focus_subjects}
                                                            onChange={handleInputChange} /><TextAreaField
                                                            label="Habits to Develop" name="habits_for_ambition"
                                                            value={formData.habits_for_ambition}
                                                            onChange={handleInputChange} /><InputField
                                                            label="I will take guidance from" name="guidance_from"
                                                            value={formData.guidance_from}
                                                            onChange={handleInputChange} /><InputField
                                                            label="This person will help by" name="help_by_person"
                                                            value={formData.help_by_person}
                                                            onChange={handleInputChange} /><InputField
                                                            label="I will learn this new thing" name="learn_new"
                                                            value={formData.learn_new}
                                                            onChange={handleInputChange} /><TextAreaField
                                                            label="How I will feel" name="feel_when_achieved"
                                                            value={formData.feel_when_achieved}
                                                            onChange={handleInputChange} /><TextAreaField
                                                            label="How my parents will feel"
                                                            name="parents_feel_when_achieved"
                                                            value={formData.parents_feel_when_achieved}
                                                            onChange={handleInputChange} /></div>
                                                </div>
                                            </div>}

                                            {activeTab !== 'activities' && (
                                                <div className="border-t border-gray-100 pt-6 mt-8 flex justify-end">
                                                    <button type="submit" disabled={saveLoading}
                                                        className="inline-flex items-center gap-2 bg-[#D67C2C] hover:bg-orange-400 text-white font-semibold px-8 py-3 rounded-xl shadow-lg disabled:opacity-70">{saveLoading ? <>
                                                            <RefreshCw className="animate-spin" /> Saving...</> : <>
                                                            <Save /> Save Changes</>}</button>
                                                </div>)}
                                        </form>

                                        {activeTab === 'activities' &&
                                            <div className="space-y-4">{activityData.length === 0 ?
                                                <div className="text-center py-12 text-gray-500">No activities recorded
                                                    yet.</div> : activityData.map((activity, index) => (<div key={index}
                                                        className="bg-white border rounded-xl p-4 flex items-start gap-4">
                                                        <div>{activity.activity_type === 'quiz' ?
                                                            <Award className="text-blue-500" /> :
                                                            <BookOpen className="text-green-500" />}</div>
                                                        <div className="flex-1"><h4
                                                            className="font-medium">{activity.description}</h4><p
                                                                className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleString()}</p>
                                                        </div>
                                                        {activity.xp_earned && <div
                                                            className="text-sm font-bold text-emerald-600">+{activity.xp_earned} XP</div>}
                                                    </div>))}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="max-w-6xl mx-auto px-4 pb-8">
                    <BeneficiaryFeedbackCard />
                </div>
                <ProfilePictureModal show={showProfilePictureModal} onClose={() => setShowProfilePictureModal(false)}
                    onUpload={handleProfilePictureUpload} uploading={uploadingPicture}
                    currentPicture={profilePicture} getInitials={getInitials} name={formData.name} />
                <ProfilePictureModal show={showFamilyPictureModal} onClose={() => setShowFamilyPictureModal(false)}
                    onUpload={handleFamilyPictureUpload} uploading={uploadingFamilyPicture}
                    currentPicture={familyPicture} getInitials={() => 'FAM'} name="Family Picture" />
            </div>
        </Layout>
    );
};

export default StudentProfilePage;
